import { useCallback, useEffect, useState } from 'react';

const validSnapshot = (value, fixtureId) => value?.requestedFixtureId === Number(fixtureId)
  && value.sourceMode === 'live' && value.sport === 'esports'
  && ['AVAILABLE', 'HIDDEN', 'NO_BROADCAST', 'NOT_ELIGIBLE'].includes(value.state)
  && /^[a-f0-9]{64}$/.test(value.stateHash)
  && Array.isArray(value.resources) && value.resources.length <= 8
  && (value.state === 'AVAILABLE' || value.resources.length === 0);

// Sole owner of broadcast requests/timers. Odds detail and BetCart lifecycle are
// deliberately not dependencies: a Writer odds replacement cannot replace this DTO.
export default function useSportsBroadcastState({
  fixtureId, eligible, enabled = true, service, sessionOwner,
}) {
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState('CHECKING');
  const [closedFixture, setClosedFixture] = useState(null);
  const closed = closedFixture != null && String(closedFixture) === String(fixtureId);
  const close = useCallback(() => setClosedFixture(fixtureId), [fixtureId]);
  const reopen = useCallback(() => setClosedFixture(null), []);

  useEffect(() => {
    let active = true, timer, expiryTimer, inFlight = null, queued = false, failures = 0;
    const visible = () => document.visibilityState !== 'hidden';
    const allowed = () => active && eligible && enabled && !closed && visible();
    const clear = () => { clearTimeout(timer); clearTimeout(expiryTimer); };
    setSnapshot(null);
    setStatus(closed ? 'CLOSED' : 'CHECKING');
    if (!eligible || !enabled || closed || !service?.getFixtureBroadcasts) return undefined;

    const run = async () => {
      clearTimeout(timer);
      if (!allowed()) return;
      if (inFlight) { queued = true; return; }
      const request = new AbortController();
      inFlight = request;
      let delay = 10_000;
      try {
        const result = await service.getFixtureBroadcasts(fixtureId, {
          signal: request.signal,
          isCurrentRequest: () => allowed() && inFlight === request,
        });
        if (!allowed() || request.signal.aborted) return;
        if (!result?.status || !validSnapshot(result.data, fixtureId)) throw Error('BROADCAST_UNAVAILABLE');
        failures = 0;
        const value = result.data;
        setSnapshot({ fixtureId, service, sessionOwner, value });
        setStatus(value.state);
        delay = value.state === 'NO_BROADCAST' ? 30_000 : 10_000;
        clearTimeout(expiryTimer);
        if (value.state === 'AVAILABLE') {
          expiryTimer = setTimeout(() => {
            if (!active) return;
            setSnapshot(null); setStatus('CHECKING');
          }, 15_000);
        }
      } catch {
        if (!allowed() || request.signal.aborted) return;
        setSnapshot(null); setStatus('UNAVAILABLE');
        delay = Math.min(30_000, 10_000 * ++failures);
      } finally {
        if (inFlight === request) inFlight = null;
        if (allowed()) {
          const immediately = queued; queued = false;
          timer = setTimeout(run, immediately ? 0 : delay + Math.floor(Math.random() * 1001));
        }
      }
    };
    const refresh = () => {
      if (!visible()) {
        clear(); setSnapshot(null); setStatus('CHECKING');
        queued = false; inFlight?.abort();
      } else if (inFlight) {
        queued = true;
      } else {
        void run();
      }
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    void run();
    return () => {
      active = false; clear(); inFlight?.abort();
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
    };
  }, [fixtureId, eligible, enabled, service, sessionOwner, closed]);

  const current = eligible && enabled && !closed
    && snapshot?.fixtureId === fixtureId && snapshot?.service === service
    && snapshot?.sessionOwner === sessionOwner;
  return { value: current ? snapshot.value : null, status: closed ? 'CLOSED' : status, closed, close, reopen };
}
